import Dialog from "components/Overlays/ResponsiveDialog";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FiUploadCloud } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoIosArrowForward } from "react-icons/io";
import FormField from "components/v2/FormField";
import TagsField from "components/v2/TagsField";
import RichTextEditor from "components/RichTextEditor";
import useEditBook from "api/BookResource/useEditBook";
import { getSubmitButtonLabel } from "api/BookResource/bookFileUpload";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import { BookFormat } from "utils/server-constants/bookFormats";
import { activeCurrencies } from "utils";

const BookEditModal = ({ visible, onHide, book, categories, genres, onUploadFiles }) => {
	const hasRoles = useAuthStore((state) => state.hasRoles);

	const { mutateAsync, isLoading } = useEditBook(book?.id, onHide);

	const initialValues = {
		title: book?.title || "",
		isbn: book?.isbn || "",
		author: book?.author || "",
		price: book?.price || "",
		zwl_price: book?.zwl_price || "",
		description: book?.description || "",
		genres: book?.genres?.[0]?.id || "",
		tags: book?.tags?.map((tag) => tag.name) || [],
		category_id: book?.category?.id || "",
		subscription: Boolean(book?.subscription),
		sellable: Boolean(book?.sellable),
		isFree: Boolean(book?.isFree),
		format: book?.format || "",
		stock_level: book?.stock_level || "",
		physical_usd_price: book?.physical_usd_price || "",
		physical_zwl_price: book?.physical_zwl_price || "",
	};

	const validationSchema = Yup.object().shape({
		title: Yup.string().required("Book title is required"),
		author: Yup.string().required("Author is required"),
		price: Yup.number().when("isFree", {
			is: true,
			then: Yup.number().notRequired(),
			otherwise: Yup.number()
				.required("Price is required")
				.moreThan(0, "Price should be greater than 0"),
		}),
		description: Yup.mixed(),
		category_id: Yup.number().required("Book category is required"),
		genres: Yup.string().required("Book genre is required"),
		format: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.string().required("Book format is required")
			: Yup.mixed().notRequired(),
		stock_level: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => ["physical", "both"].includes(format),
					then: Yup.number()
						.typeError("Stock level must be a number")
						.required("Stock level is required"),
					otherwise: Yup.mixed().notRequired(),
				})
			: Yup.mixed().notRequired(),
		physical_usd_price: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => [BookFormat.Physical, BookFormat.Both].includes(format),
					then: Yup.number()
						.typeError("Price must be a number")
						.required("Set price of the physical book"),
					otherwise: Yup.mixed().notRequired(),
				})
			: Yup.mixed().notRequired(),
		physical_zwl_price: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => [BookFormat.Physical, BookFormat.Both].includes(format),
					then: Yup.number()
						.typeError("Price must be a number")
						.required("Set price of the physical book"),
					otherwise: Yup.mixed().notRequired(),
				})
			: Yup.mixed().notRequired(),
	});

	const handleSubmit = async (data) => {
		await mutateAsync({
			...data,
			is_active: data.is_active ? 1 : 0,
			subscription: data.subscription ? 1 : 0,
			sellable: data.sellable ? 1 : 0,
			isFree: data.isFree ? 1 : 0,
		});
	};

	const handleOpenUploadFiles = () => {
		onHide?.();
		setTimeout(() => onUploadFiles?.(), 200);
	};

	return (
		<Dialog
			visible={visible}
			style={{ width: "min(720px, 95vw)" }}
			onHide={onHide}
			focusOnShow={false}
			header={
				<div>
					<p className="text-lg font-semibold text-gray-900">Edit book</p>
					{book?.title && <p className="text-sm font-normal text-gray-500 mt-0.5">{book.title}</p>}
				</div>
			}
			position="center"
			draggable={false}
			modal
			className="rounded-xl"
			contentClassName="rounded-b-xl"
		>
			<Formik
				key={book?.id}
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ setFieldValue, values, errors, touched }) => {
					const hasPhysical = [BookFormat.Physical, BookFormat.Both].includes(values?.format || "");

					return (
						<Form className="space-y-6 pt-8">
							<div className="flex gap-3">
								<div className="w-1/2">
									<FormField type="text" name="title" label="Title *" />
								</div>
								<div className="w-1/2">
									<FormField type="text" name="isbn" label="ISBN Number" />
								</div>
							</div>

							<div className="w-full">
								<FormField type="text" name="author" label="Author Name *" />
							</div>

							{hasRoles([Role.SuperAdmin, Role.Admin]) && (
								<div className={hasPhysical ? "flex flex-row gap-2" : "flex flex-col gap-6"}>
									<div className={hasPhysical ? "w-1/2" : "w-full"}>
										<FormField
											type="select"
											name="format"
											label="Book format"
											options={[
												{
													label: "Digital copies (for online reading)",
													value: "digital",
												},
												{
													label: "Physical copies",
													value: "physical",
												},
												{
													label: "Both physical and digital copies",
													value: "both",
												},
											].map((option) => ({
												label: option.label,
												value: option.value,
											}))}
										/>
									</div>
									<div className={hasPhysical ? "w-1/2" : "w-full hidden"}>
										<FormField
											type="number"
											min="0"
											step="0.01"
											name="stock_level"
											label="Number of physical copies available"
										/>
									</div>
								</div>
							)}

							<div className="flex gap-2">
								<div className="w-1/2">
									<FormField
										type="number"
										min="0"
										step="0.01"
										name="price"
										label={`Price (${activeCurrencies.USD.label})`}
									/>
								</div>
								<div className="w-1/2">
									<FormField
										type="number"
										min="0"
										step="0.01"
										name="zwl_price"
										label={`Price (${activeCurrencies.ZWL.label})`}
									/>
								</div>
							</div>

							{hasRoles([Role.SuperAdmin, Role.Admin]) && (
								<div className={` ${hasPhysical ? "flex gap-2" : "hidden"}`}>
									<div className="w-1/2">
										<FormField
											type="number"
											min="0"
											step="0.01"
											name="physical_usd_price"
											label={`Physical book price (${activeCurrencies.USD.label})`}
										/>
									</div>
									<div className="w-1/2">
										<FormField
											type="number"
											min="0"
											step="0.01"
											name="physical_zwl_price"
											label={`Physical book price (${activeCurrencies.ZWL.label})`}
										/>
									</div>
								</div>
							)}

							<div className="flex gap-2">
								<div className="w-1/2">
									<FormField
										type="select"
										name="category_id"
										label="Book category"
										options={categories?.data?.map((category) => ({
											label: category.name,
											value: category.id,
										}))}
									/>
								</div>
								<div className="w-1/2">
									<FormField
										type="select"
										name="genres"
										label="Book Genre"
										options={genres?.data?.data?.map((genre) => ({
											label: genre.name,
											value: genre.id,
										}))}
									/>
								</div>
							</div>

							<div className="w-full">
								<TagsField name="tags" label="Tags" />
							</div>

							<div className="space-y-2">
								<p className="text-gray-500">Book summary</p>
								<RichTextEditor
									resetKey={book?.id}
									value={book?.description || ""}
									onTextChange={(e) => setFieldValue("description", e.htmlValue, false)}
									error={!!(touched.description && errors.description) && errors.description}
								/>
							</div>

							<div className="space-y-2">
								<FormField
									type="switcher"
									name="subscription"
									label="Do you want people to subscribe to this book?"
								/>

								<FormField type="switcher" label="Can people buy this book?" name="sellable" />

								<FormField type="switcher" label="Make the book free?" name="isFree" />
							</div>

							<div className="border-t border-gray-300 pt-4 mt-2 flex items-center justify-between gap-3">
								{onUploadFiles && (
									<button
										type="button"
										onClick={handleOpenUploadFiles}
										className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-akgreener text-akgreener text-sm font-semibold hover:bg-akgreen/5 transition-colors"
									>
										<FiUploadCloud size={16} />
										Upload files
									</button>
								)}
								<button
									type="submit"
									disabled={isLoading}
									className="px-6 py-2.5 rounded-full bg-akgreenest hover:bg-black text-white font-semibold flex gap-2 items-center justify-center transition-all active:scale-95 disabled:opacity-70 text-sm ml-auto"
								>
									{getSubmitButtonLabel({ isLoading, mode: "edit" })}
									{isLoading && <AiOutlineLoading3Quarters size={18} className="animate-spin" />}
									{!isLoading && <IoIosArrowForward size={18} />}
								</button>
							</div>
						</Form>
					);
				}}
			</Formik>
		</Dialog>
	);
};

export default BookEditModal;
